import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { endImpersonation } from '../store/authSlice';
import { clearAuthStorage, getAccessToken, getAdminBackupToken, setAccessToken, setAdminBackupToken } from '../utils/authStorage';

import { Shield, LogOut, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8020/api';

export function ImpersonationBanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const impersonation = useSelector(s => s.auth.impersonation);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [isExiting, setIsExiting] = useState(false);
  const exitState = useRef({ pending: false, ended: false });

  useEffect(() => {
    const state = { pending: false, ended: false };
    exitState.current = state;
    setIsExiting(false);
    return () => { state.ended = true; };
  }, [impersonation]);

  const handleExit = useCallback(async (expired = false) => {
    const state = exitState.current;
    if (!impersonation || state.ended) return;

    if (!expired) {
      if (state.pending) return;
      state.pending = true;
      setIsExiting(true);
      try {
        const partnerToken = getAccessToken();
        if (!partnerToken) throw new Error('Partner session unavailable');
        const res = await fetch(`${API}/v2/admin/impersonate/end`, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${partnerToken}`,
          },
          body: JSON.stringify(impersonation.sessionId ? { session_id: impersonation.sessionId } : {}),
        });
        if (!res.ok && res.status !== 401) throw new Error('Exit failed');
        if (res.ok) await res.json();
        if (state.ended) return;
        const adminToken = getAdminBackupToken();
        if (!adminToken) throw new Error('Admin session unavailable');
        setAccessToken(adminToken);
        setAdminBackupToken(null);
        state.restoredToken = adminToken;
      } catch {
        if (!state.ended) toast.error('Could not exit partner view. Please try again.');
        return;
      } finally {
        state.pending = false;
        if (!state.ended) setIsExiting(false);
      }
    }

    // Expiry may already have restored Admin while the manual request was pending.
    if (state.ended) return;
    if (expired) {
      clearAuthStorage();
      state.restoredToken = null;
    }
    state.ended = true;
    dispatch(endImpersonation({ access_token: state.restoredToken }));
    toast.info(expired ? 'Impersonation session expired.' : 'Exited partner view.');
    navigate('/admin/partners');
  }, [dispatch, navigate, impersonation]);

  useEffect(() => {
    if (!impersonation?.expiresAt) return;
    const expiresAt = impersonation.expiresAt;

    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setSecondsLeft(diff);
      if (diff === 0) handleExit(true);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [impersonation?.expiresAt, handleExit]);

  if (!impersonation) return null;

  const mins = Math.floor((secondsLeft ?? 0) / 60);
  const secs = String((secondsLeft ?? 0) % 60).padStart(2, '0');
  const isUrgent = secondsLeft !== null && secondsLeft < 120;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: isUrgent ? '#DC2626' : '#D97706',
      color: '#fff',
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      fontSize: 14, fontWeight: 600,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Shield size={16} />
        <span>
          Admin view — accessing{' '}
          <strong>{impersonation.partnerName || 'Partner'}</strong>'s portal
        </span>
        {isUrgent && <AlertTriangle size={15} style={{ marginLeft: 4 }} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <Clock size={14} />
          <span>
            {secondsLeft !== null
              ? `${mins}:${secs} remaining`
              : 'Loading…'}
          </span>
        </div>

        <button
          onClick={() => handleExit(false)}
          disabled={isExiting}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 6, border: '2px solid rgba(255,255,255,0.7)',
            background: 'rgba(255,255,255,0.15)', color: '#fff',
            fontWeight: 700, fontSize: 13, cursor: isExiting ? 'wait' : 'pointer',
            opacity: isExiting ? 0.6 : 1,
          }}
        >
          <LogOut size={14} /> {isExiting ? 'Exiting…' : 'Exit Partner View'}
        </button>
      </div>
    </div>
  );
}
